import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Settings, Loader2, Plus, Pencil, Trash2, X, UserPlus, DollarSign, ShieldCheck } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { FaviconUpload } from '../components/FaviconUpload';

const money = (c) => `$${((c || 0) / 100).toFixed(2)}`;

const EMPTY_PAGE = { title: '', nav_label: '', intro: '', body: '', visibility: 'subgroup' };

export default function SubgroupManage() {
  const { slug } = useParams();
  const auth = useAuth();
  const [sg, setSg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [denied, setDenied] = useState(false);
  const [tab, setTab] = useState('details');

  const load = useCallback(() => {
    setLoading(true);
    auth.getSubgroup(slug).then((d) => { if (!d.is_group_admin) setDenied(true); else setSg(d); }).catch(() => setDenied(true)).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);
  useEffect(() => { load(); }, [load]);

  if (loading) return <div className="max-w-4xl mx-auto px-4 py-16 flex items-center gap-2" style={{ color: 'var(--olive)' }}><Loader2 className="animate-spin" size={16} /> Loading…</div>;
  if (denied) return <div className="max-w-2xl mx-auto px-4 py-16 text-center" data-testid="subgroup-manage-denied"><h1 className="font-title text-2xl" style={{ color: 'var(--maroon)' }}>Not authorised</h1><Link to={`/g/${slug}`} className="text-sm underline">Back to group</Link></div>;

  return (
    <div className="max-w-4xl mx-auto px-4 py-10 fade-in" data-testid="subgroup-manage">
      <Link to={`/g/${slug}`} className="text-sm underline mb-3 inline-block" style={{ color: 'var(--olive)' }}>← {sg.name}</Link>
      <div className="flex items-center gap-2 mb-1"><Settings style={{ color: 'var(--maroon)' }} /><h1 className="font-title text-3xl" style={{ color: 'var(--maroon)' }}>Manage {sg.name}</h1></div>
      <div className="flex gap-2 my-5 flex-wrap">
        {[['details', 'Details'], ['pages', 'Pages'], ['members', 'Members']].map(([id, label]) => (
          <button key={id} onClick={() => setTab(id)} data-testid={`sgm-tab-${id}`}
            className="px-3 py-1.5 rounded text-sm font-title" style={{ background: tab === id ? 'var(--maroon)' : 'var(--sage)', color: tab === id ? 'var(--cream)' : 'var(--ink)' }}>{label}</button>
        ))}
      </div>
      {tab === 'details' && <DetailsTab sg={sg} slug={slug} auth={auth} onSaved={load} />}
      {tab === 'pages' && <PagesTab slug={slug} auth={auth} />}
      {tab === 'members' && <MembersTab sg={sg} slug={slug} auth={auth} />}
    </div>
  );
}

function DetailsTab({ sg, slug, auth, onSaved }) {
  const isMainAdmin = auth.user && auth.user.memberClass === 'admin';
  const canPublish = auth.user && ['admin', 'site-admin'].includes(auth.user.memberClass);
  const sgStatus = sg.status || (sg.active ? 'active' : 'inactive');
  const [form, setForm] = useState({ name: sg.name || '', tagline: sg.tagline || '', intro: sg.intro || '', body: sg.body || '', favicon_url: sg.favicon_url || '', join_fee: ((sg.join_fee_cents || 0) / 100).toFixed(2), active: sg.active });
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');

  const save = async () => {
    setBusy(true); setMsg('');
    const body = { name: form.name, tagline: form.tagline, intro: form.intro, body: form.body, favicon_url: form.favicon_url };
    if (canPublish) body.active = form.active;
    if (isMainAdmin) body.join_fee_cents = Math.round(parseFloat(form.join_fee || 0) * 100) || 0;
    try { await auth.updateSubgroup(slug, body); setMsg('Saved.'); onSaved(); } catch (e) { setMsg('Could not save.'); } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-lg border shadow-sm p-5 space-y-3" style={{ borderColor: 'rgba(0,0,0,0.08)' }} data-testid="sgm-details">
      <Field label="Group name"><input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="sgm-name" className="w-full px-3 py-2 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} /></Field>
      <Field label="Tagline"><input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} data-testid="sgm-tagline" className="w-full px-3 py-2 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} /></Field>
      <Field label="Intro (plain text)"><textarea value={form.intro} onChange={(e) => setForm({ ...form, intro: e.target.value })} rows={2} data-testid="sgm-intro" className="w-full px-3 py-2 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} /></Field>
      <Field label="Home page body (HTML allowed)"><textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={5} data-testid="sgm-body" className="w-full px-3 py-2 rounded border text-sm font-mono" style={{ borderColor: 'rgba(0,0,0,0.2)' }} /></Field>
      <Field label="Favicon (browser-tab icon for this group)">
        <FaviconUpload value={form.favicon_url} onChange={(url) => setForm({ ...form, favicon_url: url })} upload={(d, ct) => auth.sgUploadImage(slug, d, ct)} testid="sgm-favicon-upload" />
        <input value={form.favicon_url} onChange={(e) => setForm({ ...form, favicon_url: e.target.value })} placeholder="…or paste an image URL" data-testid="sgm-favicon" className="w-full mt-2 px-3 py-2 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} />
      </Field>
      <div className="flex items-center gap-4 flex-wrap">
        <Field label="Annual fee ($)">
          <input value={form.join_fee} onChange={(e) => setForm({ ...form, join_fee: e.target.value })} type="number" step="0.01" min="0" disabled={!isMainAdmin} data-testid="sgm-fee"
            className="w-32 px-3 py-2 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)', opacity: isMainAdmin ? 1 : 0.6 }} />
        </Field>
        {canPublish ? (
          <label className="flex items-center gap-2 text-sm mt-5"><input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} data-testid="sgm-active" className="accent-[var(--maroon)]" /> Active (live to members)</label>
        ) : (
          <div className="mt-5 text-sm" data-testid="sgm-status">
            <span className="text-xs font-title px-2 py-1 rounded-full" style={{ background: sgStatus === 'active' ? '#1f7a3f' : sgStatus === 'pending' ? '#8a5a00' : '#8a1f11', color: '#fff' }}>
              {sgStatus === 'active' ? 'Live' : sgStatus === 'pending' ? 'Awaiting approval' : 'Not live'}
            </span>
            {sgStatus === 'pending' && <span className="ml-2" style={{ color: 'var(--olive)' }}>A site administrator will approve it before it goes live.</span>}
          </div>
        )}
      </div>
      {!isMainAdmin && <p className="text-xs" style={{ color: 'var(--olive)' }}>Only a main site administrator can change the membership fee.</p>}
      <div className="flex items-center gap-3">
        <button onClick={save} disabled={busy} data-testid="sgm-save" className="inline-flex items-center gap-1 px-4 py-2 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: busy ? 0.7 : 1 }}>{busy && <Loader2 size={14} className="animate-spin" />} Save</button>
        {msg && <span className="text-sm" style={{ color: 'var(--olive)' }}>{msg}</span>}
      </div>
    </div>
  );
}

function PagesTab({ slug, auth }) {
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { setLoading(true); auth.sgAdminListPages(slug).then((p) => setPages(Array.isArray(p) ? p : [])).catch(() => setPages([])).finally(() => setLoading(false)); }, [auth, slug]);
  useEffect(() => { load(); }, [load]);

  const save = async () => {
    if (!form.title.trim()) return;
    setBusy(true);
    try {
      if (form.page_key) await auth.sgUpdatePage(slug, form.page_key, form);
      else await auth.sgCreatePage(slug, form);
      setForm(null); load();
    } finally { setBusy(false); }
  };
  const del = async (p) => { if (!window.confirm(`Delete page "${p.title}"?`)) return; await auth.sgDeletePage(slug, p.page_key); load(); };

  return (
    <div data-testid="sgm-pages">
      <button onClick={() => setForm({ ...EMPTY_PAGE })} data-testid="sgm-add-page" className="inline-flex items-center gap-1 px-3 py-2 rounded text-sm font-title mb-3" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}><Plus size={15} /> Add page</button>
      <div className="bg-white rounded-lg border shadow-sm overflow-hidden" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
        <table className="w-full text-sm">
          <thead><tr style={{ background: 'var(--navy)' }}><th className="px-3 py-2.5 text-left font-title" style={{ color: 'var(--cream)' }}>Title</th><th className="px-3 py-2.5 text-left font-title" style={{ color: 'var(--cream)' }}>Visibility</th><th className="px-3 py-2.5 text-right font-title" style={{ color: 'var(--cream)' }}>Actions</th></tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={3} className="px-3 py-4 text-center"><Loader2 className="animate-spin inline" size={16} /></td></tr>
            : pages.length === 0 ? <tr><td colSpan={3} className="px-3 py-4 text-center" style={{ color: 'var(--olive)' }}>No pages yet.</td></tr>
            : pages.map((p) => (
              <tr key={p.page_key} className="border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }} data-testid={`sgm-page-row-${p.page_key}`}>
                <td className="px-3 py-2 font-title" style={{ color: 'var(--navy)' }}>{p.title}</td>
                <td className="px-3 py-2 capitalize">{p.visibility === 'subgroup' ? 'Group members' : p.visibility === 'members' ? 'Logged-in' : 'Public'}</td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => setForm({ page_key: p.page_key, title: p.title, nav_label: p.nav_label || '', intro: p.intro || '', body: p.body || '', visibility: p.visibility || 'subgroup' })} data-testid={`sgm-edit-page-${p.page_key}`} className="mr-2"><Pencil size={15} style={{ color: 'var(--navy)' }} /></button>
                  <button onClick={() => del(p)} data-testid={`sgm-del-page-${p.page_key}`}><Trash2 size={15} style={{ color: '#8a1f11' }} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {form && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: 'rgba(0,0,0,0.5)' }} data-testid="sgm-page-modal">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-3 border-b" style={{ borderColor: 'rgba(0,0,0,0.08)' }}><h3 className="font-title text-lg" style={{ color: 'var(--maroon)' }}>{form.page_key ? 'Edit page' : 'Add page'}</h3><button onClick={() => setForm(null)}><X size={18} /></button></div>
            <div className="p-5 space-y-3">
              <Field label="Title"><input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="sgm-page-title" className="w-full px-3 py-2 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} /></Field>
              <Field label="Nav label (optional)"><input value={form.nav_label} onChange={(e) => setForm({ ...form, nav_label: e.target.value })} data-testid="sgm-page-nav" className="w-full px-3 py-2 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} /></Field>
              <Field label="Intro"><textarea value={form.intro} onChange={(e) => setForm({ ...form, intro: e.target.value })} rows={2} data-testid="sgm-page-intro" className="w-full px-3 py-2 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} /></Field>
              <Field label="Body (HTML allowed)"><textarea value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} rows={5} data-testid="sgm-page-body" className="w-full px-3 py-2 rounded border text-sm font-mono" style={{ borderColor: 'rgba(0,0,0,0.2)' }} /></Field>
              <Field label="Who can view this page">
                <select value={form.visibility} onChange={(e) => setForm({ ...form, visibility: e.target.value })} data-testid="sgm-page-visibility" className="w-full px-3 py-2 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }}>
                  <option value="public">Public — everyone</option>
                  <option value="members">Logged-in members</option>
                  <option value="subgroup">This group's members only</option>
                </select>
              </Field>
            </div>
            <div className="flex justify-end gap-2 px-5 py-3 border-t" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
              <button onClick={() => setForm(null)} className="px-4 py-2 rounded text-sm" style={{ background: 'var(--sage)' }}>Cancel</button>
              <button onClick={save} disabled={busy} data-testid="sgm-page-save" className="inline-flex items-center gap-1 px-4 py-2 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)', opacity: busy ? 0.7 : 1 }}>{busy && <Loader2 size={14} className="animate-spin" />} Save</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MembersTab({ sg, slug, auth }) {
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [email, setEmail] = useState('');
  const [err, setErr] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => { setLoading(true); auth.sgListMembers(slug).then((m) => setMembers(Array.isArray(m) ? m : [])).catch(() => setMembers([])).finally(() => setLoading(false)); }, [auth, slug]);
  useEffect(() => { load(); }, [load]);

  const add = async () => {
    if (!email.trim()) return;
    setBusy(true); setErr('');
    try { await auth.sgAddMember(slug, { email: email.trim(), role: 'member' }); setEmail(''); load(); }
    catch (e) { setErr((e.response && e.response.data && e.response.data.detail) || 'Could not add member.'); }
    finally { setBusy(false); }
  };
  const setRole = async (uid, role) => { await auth.sgUpdateMember(slug, uid, { role }); load(); };
  const setStatus = async (uid, status) => { await auth.sgUpdateMember(slug, uid, { status }); load(); };
  const recordPayment = async (uid) => { await auth.sgRecordPayment(slug, uid); load(); };
  const remove = async (uid, name) => { if (!window.confirm(`Remove ${name} from this group?`)) return; await auth.sgRemoveMember(slug, uid); load(); };

  return (
    <div data-testid="sgm-members">
      <div className="bg-white rounded-lg border shadow-sm p-4 mb-4" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
        <label className="block text-sm font-title mb-1" style={{ color: 'var(--navy)' }}>Add a member by email</label>
        <div className="flex gap-2">
          <input value={email} onChange={(e) => setEmail(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && add()} placeholder="member@example.com" data-testid="sgm-add-member-email" className="flex-1 px-3 py-2 rounded border text-sm" style={{ borderColor: 'rgba(0,0,0,0.2)' }} />
          <button onClick={add} disabled={busy} data-testid="sgm-add-member-btn" className="inline-flex items-center gap-1 px-3 py-2 rounded text-sm font-title" style={{ background: 'var(--maroon)', color: 'var(--cream)' }}><UserPlus size={15} /> Add</button>
        </div>
        {err && <p className="text-xs mt-1" style={{ color: '#8a1f11' }} data-testid="sgm-add-member-error">{err}</p>}
        <p className="text-xs mt-1" style={{ color: 'var(--olive)' }}>The person must already have a portal account.</p>
      </div>

      <div className="bg-white rounded-lg border shadow-sm overflow-x-auto" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
        <table className="w-full text-sm">
          <thead><tr style={{ background: 'var(--navy)' }}>
            <th className="px-3 py-2.5 text-left font-title" style={{ color: 'var(--cream)' }}>Member</th>
            <th className="px-3 py-2.5 text-left font-title" style={{ color: 'var(--cream)' }}>Role</th>
            <th className="px-3 py-2.5 text-left font-title" style={{ color: 'var(--cream)' }}>Status</th>
            <th className="px-3 py-2.5 text-left font-title" style={{ color: 'var(--cream)' }}>Last paid</th>
            <th className="px-3 py-2.5 text-right font-title" style={{ color: 'var(--cream)' }}>Actions</th>
          </tr></thead>
          <tbody>
            {loading ? <tr><td colSpan={5} className="px-3 py-4 text-center"><Loader2 className="animate-spin inline" size={16} /></td></tr>
            : members.length === 0 ? <tr><td colSpan={5} className="px-3 py-4 text-center" style={{ color: 'var(--olive)' }}>No members yet.</td></tr>
            : members.map((m) => (
              <tr key={m.user_id} className="border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }} data-testid={`sgm-member-row-${m.user_id}`}>
                <td className="px-3 py-2"><div className="font-title" style={{ color: 'var(--navy)' }}>{m.name || '—'}</div><div className="text-xs" style={{ color: 'var(--olive)' }}>{m.email}</div></td>
                <td className="px-3 py-2">
                  <select value={m.role} onChange={(e) => setRole(m.user_id, e.target.value)} data-testid={`sgm-role-${m.user_id}`} className="px-2 py-1 rounded border text-xs" style={{ borderColor: 'rgba(0,0,0,0.2)' }}>
                    <option value="member">Member</option><option value="admin">Group admin</option>
                  </select>
                </td>
                <td className="px-3 py-2">
                  <select value={m.status} onChange={(e) => setStatus(m.user_id, e.target.value)} data-testid={`sgm-status-${m.user_id}`} className="px-2 py-1 rounded border text-xs" style={{ borderColor: 'rgba(0,0,0,0.2)' }}>
                    <option value="financial">Financial</option><option value="nonfinancial">Non-financial</option>
                  </select>
                </td>
                <td className="px-3 py-2 text-xs">{m.date_last_paid || '—'}</td>
                <td className="px-3 py-2 text-right whitespace-nowrap">
                  {sg.join_fee_cents > 0 && <button onClick={() => recordPayment(m.user_id)} title="Record payment" data-testid={`sgm-record-${m.user_id}`} className="mr-2"><DollarSign size={15} style={{ color: '#1f7a3f' }} /></button>}
                  <button onClick={() => remove(m.user_id, m.name || m.email)} title="Remove" data-testid={`sgm-remove-${m.user_id}`}><Trash2 size={15} style={{ color: '#8a1f11' }} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs mt-2 flex items-center gap-1" style={{ color: 'var(--olive)' }}><ShieldCheck size={13} /> Records a payment for {money(sg.join_fee_cents)} against the current year.</p>
    </div>
  );
}

function Field({ label, children }) {
  return (<div><label className="block text-sm font-title mb-1" style={{ color: 'var(--navy)' }}>{label}</label>{children}</div>);
}
